import type { PDPProductThumbnail } from "@components/commerce/product/right-panel/buy-card/types";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { getImageAltTag } from "@lib/utils/attributesUtils";
import { Text } from "@ecco/ui/text";
import { Box, type BoxProps } from "@ecco/ui/box";
import type { CSSObject } from "@emotion/react";
import { memo, useMemo } from "react";
import { ProductImageFrame } from "@ecco/ui/product-image-frame";
import { getImageBoxStyles } from "./ProductBuyCardImagesStyles";
import { useStore } from "@store/store";
import { useLocale } from "next-intl";
import { getPDPSlug } from "@lib/utils/getPDPSlug";
import { NextLink } from "@components/ui/next-link/NextLink";
import { Flex } from "@ecco/ui/flex";
import { Stack } from "@ecco/ui/stack";
import { scrollbarStyles } from "@ecco/ui/css/scrollbar";
import { useActiveVariant } from "@lib/product/useActiveVariant";

const ProductThumbnail = memo(
    ({
        thumbnail,
        isSelected,
        locale,
        onClick,
        isLoading = false,
    }: {
        thumbnail: PDPProductThumbnail;
        isSelected: boolean;
        locale: string;
        onClick: () => void;
        isLoading?: boolean;
    }) => {
        const url = getPDPSlug(
            thumbnail.productName,
            thumbnail.articleNumber,
            thumbnail.colorCode,
            locale
        ).slug;

        return (
            <NextLink
                href={url}
                onClick={onClick}
                aria-label={`Select ${thumbnail.dominantColor} Color Variant`}
            >
                <Box {...(getImageBoxStyles(isSelected, isLoading) as BoxProps)}>
                    <ProductImageFrame
                        src={thumbnail.image.url}
                        alt={getImageAltTag(
                            `${thumbnail.pageTitle ?? thumbnail.productName} - ${thumbnail.dominantColor}`,
                            thumbnail.image.label
                        )}
                        sizes={["25vw", "25vw", "25vw", "8vw"]}
                    />
                </Box>
            </NextLink>
        );
    }
);

ProductThumbnail.displayName = "ProductThumbnail";

interface ProductBuyCardImagesProps {
    productImageThumbnails: PDPProductThumbnail[];
    productColorLabel: string;
    isFetchingProduct?: boolean;
}

const ProductBuyCardImages = memo(
    ({
        productImageThumbnails,
        productColorLabel,
        isFetchingProduct = false,
    }: ProductBuyCardImagesProps) => {
        const { get: getMicrocopy } = useMicrocopy();
        const setActiveProduct = useStore((state) => state.setActiveProduct);
        const locale = useLocale();

        const { colorCode } = useActiveVariant();

        const activeThumbnail = productImageThumbnails.find(
            (thumb) => thumb.colorCode === colorCode
        );

        const displayColorLabel = activeThumbnail?.dominantColor ?? productColorLabel;

        const handleColorChange = (thumbnail: PDPProductThumbnail) => {
            if (thumbnail.colorCode === colorCode) return;
            setActiveProduct({
                colorCode: thumbnail.colorCode,
                variant: null,
                isAvailable: false,
            });
        };

        const parsedThumbnails = useMemo(
            () =>
                [...productImageThumbnails]
                    .sort((a, b) =>
                        a.colorCode.localeCompare(b.colorCode, undefined, {
                            numeric: true,
                            sensitivity: "base",
                        })
                    )
                    .filter((thumb) => Boolean(thumb.available)),
            [productImageThumbnails]
        );

        return (
            <Stack gap="2" data-testid="product-buy-images" mb="6">
                <Text fontWeight="bold" size="normal" display={{ base: "none", lg: "block" }}>
                    {getMicrocopy(CommonCMS.shared, CommonCMS.color)}
                </Text>

                <Box
                    css={
                        {
                            ...scrollbarStyles,
                            "::-webkit-scrollbar": { width: "0px", height: "0px" },
                        } as CSSObject
                    }
                    marginLeft={{ base: "-15px", lg: "0" }}
                    marginRight={{ base: "-15px", lg: "0" }}
                    paddingLeft={{ base: "15px", lg: "0" }}
                    overflow={{ base: "auto", lg: "initial" }}
                >
                    <Flex flexDir="row" gap="1px" flexWrap={{ base: "nowrap", lg: "wrap" }}>
                        {parsedThumbnails.map((thumb) => (
                            <ProductThumbnail
                                key={`${thumb.articleNumber}${thumb.colorCode}`}
                                thumbnail={thumb}
                                locale={locale}
                                isSelected={colorCode === thumb.colorCode}
                                onClick={() => handleColorChange(thumb)}
                                isLoading={isFetchingProduct}
                            />
                        ))}
                    </Flex>
                </Box>

                <Text size="b4">{displayColorLabel}</Text>
            </Stack>
        );
    }
);

ProductBuyCardImages.displayName = "ProductBuyCardImages";

export default ProductBuyCardImages;
